import {createContext, Dispatch, SetStateAction, useContext} from 'react'
import {localStorageTemplateSelectedType} from '../features'
import {ProductModel} from '../modules/auth/models/UserModel'
import {SAVResponsesTicket} from '../@types'
import {triggerReloadInterface} from '../@types/global'
import {LoadingState} from '../../global'

/**
 * Driver Context
 */

type PageKey = 'dashboard' | 'mailing' | 'callTracking' | 'webForms' | 'support' | 'websiteUpdates'


type DriverStateType = {
  pages: Record<PageKey, boolean> // === { dashboard: boolean, mailing: boolean, callTracking: boolean }
  lastCompleted?: Date
  version: string // For migration management
}
const initialDriverState: DriverStateType = {
  pages: {
    dashboard: false,
    mailing: false,
    callTracking: false,
    webForms: false,
    support: false,
    websiteUpdates: false,
  },
  version: '1.0.0',
}

/**
 * Notifications Context
 */

type ToastProps = {
  type: 'success' | 'error' | ''
  text: {
    title: string
    message: string
  }
}

export interface NotificationData {
  message: string;
  model_id: string;
  action_url: string;
}

export interface NotificationItem {
  id: number;
  data: NotificationData;
  created_at: string;
  read_at: string | null;
}

export interface Notifications {
  count: number;
  data: NotificationItem[];
}

interface EnhancedTicket extends SAVResponsesTicket {
  ticket_id: string | number
}

interface TicketMessage {
  count: number
  data: EnhancedTicket[]
}

interface UserNotificationsResponse {
  count: number
  status: number
  message_code: string
  ticket_message: TicketMessage
  notifications: Notifications;
}

interface NotificationsContextInterface {
  toast: ToastProps
  setToast: Dispatch<SetStateAction<ToastProps>>
  userNotifications: Partial<UserNotificationsResponse>
  refreshNotifications: () => void
  refetch: () => void
  setLoading: Dispatch<SetStateAction<LoadingState>>
  loading: LoadingState
}

const NotificationsContext = createContext<NotificationsContextInterface | null>(null)

const initialToast: ToastProps = {
  type: '',
  text: {
    title: '',
    message: '',
  },
}

const initialNotificationsContext: NotificationsContextInterface = {
  toast: initialToast,
  setToast: () => {
  },
  userNotifications: {
    ticket_message: {
      count: 0,
      data: [],
    },
  },
  refreshNotifications: () => {
  },
  refetch: async () => {
  },
  setLoading: () => {
  },
  loading: 'idle'
}

const useNotifications = () => {
  const context = useContext(NotificationsContext)
  if (!context) throw new Error('useNotifications must be used within NotificationsContext')
  return context
}

/**
 * Storage Context
 */

type campaignType = {
  campaign: localStorageTemplateSelectedType
}

interface StorageContextInterface {
  mailing: campaignType
  setMailing: (campaign: campaignType) => void
}

const initialValueMailing: campaignType = {
  campaign: {
    template: {
      id: '',
      name: '',
      content: '',
    },
  },
}

const StorageContext = createContext<StorageContextInterface | null>(null)

const useStorage = () => {
  const context = useContext(StorageContext)
  if (!context) throw new Error('useStorage must be used within StorageContext')
  return context
}

/**
 * Products Context
 */

interface ProductsObjectInterface {
  callTracking: ProductModel
  contacts: ProductModel
  conversionClicks: ProductModel
  documents: ProductModel
  reputation: ProductModel
  mailing: ProductModel
  sav: ProductModel
  statistics: ProductModel
  updatePlugins: ProductModel
  webForms: ProductModel
  reviews: ProductModel
}

interface ProductsContextInterface extends triggerReloadInterface {
  products: ProductsObjectInterface
  requestProducts: PropsProducts[]
  loadingState: LoadingState
}

const ProductsContext = createContext<ProductsContextInterface | null>(null)

type RequestProduct = {
  id: number
  products: string
  trait: number
}

type RequestProducts = {
  [key: string]: RequestProduct
}

type PropsProducts = {
  name: string
  date: string
  description: string
  status: boolean
  treaty: number
}

const useProducts = () => {
  const context = useContext(ProductsContext)
  if (!context) throw new Error('useProducts must be used within ProductsContext')
  return context
}
const initialValuesProducts: ProductsContextInterface = {
  reloadKey: 0,
  triggerReload: () => {
  },
  products: {
    callTracking: {id: 0, name: '', description: null, activated_at: null, display_name: '', encrypted: null},
    mailing: {id: 0, name: '', description: null, activated_at: null, display_name: '', encrypted: null},
    reputation: {id: 0, name: '', description: null, activated_at: null, display_name: '', encrypted: null},
    sav: {id: 0, name: '', description: null, activated_at: null, display_name: '', encrypted: null},
    statistics: {id: 0, name: '', description: null, activated_at: null, display_name: '', encrypted: null},
    updatePlugins: {id: 0, name: '', description: null, activated_at: null, display_name: '', encrypted: null},
    webForms: {id: 0, name: '', description: null, activated_at: null, display_name: '', encrypted: null},
    conversionClicks: {id: 0, name: '', description: null, activated_at: null, display_name: '', encrypted: null},
    documents: {id: 0, name: '', description: null, activated_at: null, display_name: '', encrypted: null},
    contacts: {id: 0, name: '', description: null, activated_at: '', display_name: '', encrypted: null},
    reviews: {id: 0, name: '', description: null, activated_at: '', display_name: '', encrypted: null},
  },
  requestProducts: [],
  loadingState: 'idle',
}


export {
  initialDriverState,
  initialToast,
  StorageContext,
  NotificationsContext,
  initialValueMailing,
  ProductsContext,
  initialValuesProducts,
  initialNotificationsContext,
  useStorage,
  useProducts,
  useNotifications,
}

export type {
  PageKey,
  DriverStateType,
  ToastProps,
  EnhancedTicket,
  TicketMessage,
  UserNotificationsResponse,
  campaignType,
  StorageContextInterface,
  ProductsObjectInterface,
  ProductsContextInterface,
  RequestProduct,
  RequestProducts,
  PropsProducts,
  NotificationsContextInterface,
}